import { createFileRoute } from '@tanstack/react-router'
import {
  ArrowDown,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Download,
  GraduationCap,
  Instagram,
  Map as MapIcon,
  MessageCircleQuestion,
  Sparkles,
} from 'lucide-react'
import { lazy, Suspense, useState } from 'react'
import { ContactForm } from '../components/ContactForm'
import { Header } from '../components/Header'
import { SectionHeading } from '../components/SectionHeading'
import { WorldMap } from '../components/WorldMap'
import { comic, links } from '../lib/comic'

const ComicReader = lazy(() =>
  import('../components/ComicReader').then((module) => ({ default: module.ComicReader })),
)

export const Route = createFileRoute('/')({
  component: HomePage,
})

function HomePage() {
  const [readerOpen, setReaderOpen] = useState(false)

  const openReader = () => {
    setReaderOpen(true)
    window.setTimeout(
      () => document.getElementById('reader-frame')?.scrollIntoView({ behavior: 'smooth' }),
      0,
    )
  }

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to the story
      </a>
      <Header />
      <main id="main-content">
        <section className="hero" id="top">
          <h1 className="sr-only">The Nahw Tales: The Key of Kalaam, Volume 1</h1>
          <div className="hero__city" aria-hidden="true" />
          <div className="hero__portal" aria-hidden="true" />
          <div className="hero__inner shell">
            <div className="hero__cover-wrap">
              <span className="hero__issue">Vol. 1</span>
              <img
                className="hero__cover"
                src="/assets/comic/cover.webp"
                alt="Cover of The Nahw Tales: The Key of Kalaam, Volume 1"
                width="994"
                height="1386"
              />
            </div>
            <div className="hero__copy">
              <p className="eyebrow">A comic-book adventure through Arabic grammar</p>
              <img
                className="hero__logo"
                src="/assets/brand/title-logo.webp"
                alt="The Nahw Tales: The Key of Kalaam"
              />
              <p className="hero__lede">
                Journey into the Land of Mufeed, where words have power, grammar shapes the world,
                and Major Kalaam and the ghouls threaten to plunge everything into darkness.
              </p>
              <div className="hero__credits">
                <span>Written by {comic.writers.join(', ')}</span>
                <span>Illustrated by {comic.illustrator}</span>
              </div>
              <div className="hero__actions">
                <button className="button button--primary" type="button" onClick={openReader}>
                  Read the comic <BookOpen aria-hidden="true" />
                </button>
                <a className="button button--ghost" href={links.download}>
                  Download PDF <Download aria-hidden="true" />
                </a>
              </div>
              <p className="hero__file-note">
                Complete educational edition · 88 pages · {comic.pdfSizeLabel}
              </p>
            </div>
          </div>
          <a className="hero__scroll" href="#story">
            Enter the story <ArrowDown aria-hidden="true" />
          </a>
        </section>

        <section className="story section section--paper" id="story">
          <div className="shell">
            <SectionHeading kicker="Inside Volume 1">
              Grammar has never had stakes like these.
            </SectionHeading>
            <div className="story__intro">
              <p className="story__lead">
                A long, long time ago, humanity strove for excellence in Arabic. We built the very
                foundations upon which the world now stands. There is much to learn—if you are brave
                enough to seek it.
              </p>
              <p>
                The Nahw Tales turns foundational ideas in Arabic grammar into places, characters,
                alliances and danger. It is part adventure story, part visual learning world, made
                to invite curious readers into the language.
              </p>
            </div>
            <div className="story-cards">
              <article className="story-card story-card--red">
                <Sparkles aria-hidden="true" />
                <span>01</span>
                <h3>Adventure first</h3>
                <p>A portal opens. A world is at risk. The lesson begins inside the action.</p>
              </article>
              <article className="story-card story-card--cyan">
                <GraduationCap aria-hidden="true" />
                <span>02</span>
                <h3>Arabic made visible</h3>
                <p>Abstract rules become memorable heroes, regions and rival forces.</p>
              </article>
              <article className="story-card story-card--navy">
                <MapIcon aria-hidden="true" />
                <span>03</span>
                <h3>The Land of Mufeed</h3>
                <p>A full illustrated world map connects the paths of Nahw and meaning.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="read-section section" id="read">
          <div className="read-section__art" aria-hidden="true">
            <img src="/assets/comic/back-cover.webp" alt="" />
          </div>
          <div className="shell read-section__inner">
            <div className="read-section__copy">
              <p className="eyebrow">Read for free</p>
              <h2>
                Open the portal.
                <br />
                Turn the page.
              </h2>
              <p>
                Read all 88 pages online, or keep the complete educational edition on your device.
              </p>
              {!readerOpen ? (
                <button className="button button--primary" type="button" onClick={openReader}>
                  Launch the reader <ArrowRight aria-hidden="true" />
                </button>
              ) : null}
              <a className="read-section__download" href={links.download}>
                <Download aria-hidden="true" /> Download PDF <span>{comic.pdfSizeLabel}</span>
              </a>
            </div>
            <div className="read-section__covers" aria-hidden="true">
              <img src="/assets/comic/cover.webp" alt="" />
              <img src="/assets/comic/back-cover.webp" alt="" />
            </div>
          </div>
          {readerOpen ? (
            <div className="shell reader-frame" id="reader-frame">
              <Suspense fallback={<div className="reader-suspense">Preparing the reader…</div>}>
                <ComicReader onClose={() => setReaderOpen(false)} />
              </Suspense>
            </div>
          ) : null}
        </section>

        <section className="sketch section" id="sketch">
          <img
            className="sketch__background"
            src="/assets/sketch/original-characters.webp"
            alt=""
          />
          <div className="shell sketch__inner">
            <div className="sketch__copy">
              <SectionHeading kicker="From the drawing board">
                Every hero starts as a line.
              </SectionHeading>
              <p>
                Before the colour, the city and the portal, there was the original character sketch:
                raw expressions, silhouettes and the first spark of the Fantastic Five.
              </p>
              <span className="sketch__note">Original concept artwork</span>
            </div>
            <img
              className="sketch__feature"
              src="/assets/sketch/original-characters.webp"
              alt="Original black-and-white character sketches from The Nahw Tales"
              loading="lazy"
            />
          </div>
        </section>

        <WorldMap />

        <section className="creators section" id="creators">
          <div className="shell creators__inner">
            <div>
              <SectionHeading kicker="Meet the creators">
                Built by storytellers and educators.
              </SectionHeading>
              <div className="creator-list">
                <div>
                  <span>Written by</span>
                  {comic.writers.map((writer) => (
                    <strong key={writer}>{writer}</strong>
                  ))}
                </div>
                <div>
                  <span>Illustrated by</span>
                  <strong>{comic.illustrator}</strong>
                </div>
              </div>
            </div>
            <img
              className="creators__mark"
              src="/assets/brand/publisher-mark.webp"
              alt="Leoladi publisher mark"
              loading="lazy"
            />
          </div>
        </section>

        <section className="contact section section--paper" id="contact">
          <div className="shell contact__inner">
            <div className="contact__copy">
              <MessageCircleQuestion aria-hidden="true" />
              <p className="eyebrow">Corrections or questions?</p>
              <h2>Help us make the next edition even stronger.</h2>
              <p>
                Spotted something? Teaching with the comic? Curious about the world? Send the team a
                note.
              </p>
              <ul>
                <li>
                  <CheckCircle2 aria-hidden="true" /> Corrections and feedback
                </li>
                <li>
                  <CheckCircle2 aria-hidden="true" /> Educator enquiries
                </li>
                <li>
                  <CheckCircle2 aria-hidden="true" /> Press and collaborations
                </li>
              </ul>
            </div>
            <ContactForm />
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="shell footer__inner">
          <img className="footer__logo" src="/assets/brand/title-logo.webp" alt="The Nahw Tales" />
          <div className="footer__links">
            <a href="#read">Read the comic</a>
            <a href={links.download}>Download PDF</a>
            <a href={links.instagram} target="_blank" rel="noreferrer">
              <Instagram aria-hidden="true" /> Instagram
            </a>
          </div>
          <p>Preview &amp; Educational Copy · Copyright © 2026 The Nahw Tales</p>
        </div>
      </footer>
    </>
  )
}
